<template>
  <p-nav id="navbar" class="d-none-mobile-app">
    <p-nav-logo :href="rootUrl" class="xl:!mr-8 !mr-4">
      <span class="sr-only">remove.bg</span>
      <p-logo-rbg />
    </p-nav-logo>
    <ul class="hidden xl:flex flex-grow items-center gap-4 xl:gap-8 my-0">
      <li>
        <p-nav-link :href="newImageUrl">
          {{ I18n.t("navigation.remove_background") }}
        </p-nav-link>
      </li>
      <li>
        <p-dropdown>
          <template #trigger>
            <p-nav-link as="span" show-chevron href="#">
              {{ I18n.t("navigation.how_to_use") }}
            </p-nav-link>
          </template>

          <template #content>
            <p-dropdown-box>
              <p-sub-nav-link v-for="targetGroup in targetGroups" :key="targetGroup.title" :href="targetGroup.href">
                <template #icon>
                  <component :is="targetGroup.icon" />
                </template>
                <template #title>{{ targetGroup.title }}</template>
              </p-sub-nav-link>
            </p-dropdown-box>
          </template>
        </p-dropdown>
      </li>
      <li>
        <p-nav-link :href="toolsUrl">
          {{ I18n.t("navigation.tools_api") }}
        </p-nav-link>
      </li>
      <li>
        <p-nav-link :href="pricingUrl">
          {{ I18n.t("navigation.pricing") }}
          <p-badge v-if="blackFriday" class="ml-1" tone="attentive">Black Friday</p-badge>
        </p-nav-link>
      </li>
    </ul>

    <ul class="hidden flex-none xl:flex items-center gap-4 xl:gap-8 my-0">
      <li v-if="!signedIn">
        <p-nav-link :href="loginUrl">
          {{ I18n.t("navigation.login") }}
        </p-nav-link>
      </li>
      <li v-if="!signedIn">
        <p-button variant="secondary" :href="signupUrl" as="link">
          {{ I18n.t("navigation.signup") }}
        </p-button>
      </li>
      <div v-if="signedIn">
        <p-dropdown align-right :on-open="loadCredits">
          <template #trigger>
            <p-avatar>{{ initals }}</p-avatar>
          </template>
          <template #content>
            <div class="!p-6 flex flex-col gap-y-4" style="width: 260px">
              <span class="text-typo-secondary font-bold">{{ displayName }}</span>
              <div class="flex flex-col gap-y-1">
                <span class="text-brand-typo font-bold text-2xl">
                  {{ I18n.t("dashboard.credits", { count: credits }) }}
                </span>
                <p-link :href="pricingUrl" hide-arrow>{{ I18n.t("dashboard.buy_credits_or_subscribe") }}</p-link>
              </div>
              <p-nav-link height="sm" :href="accountUrl">{{ I18n.t("navigation.my_account") }}</p-nav-link>
              <p-nav-link height="sm" :href="logoutUrl" data-method="delete">{{ I18n.t("navigation.logout") }}</p-nav-link>
            </div>
          </template>
        </p-dropdown>
      </div>
    </ul>

    <div class="flex flex-none xl:hidden items-center">
      <a :href="pricingUrl" class="no-underline">
        <p-badge v-if="blackFriday" class="ml-1 hidden sm:block" tone="attentive">Black Friday</p-badge>
      </a>
      <p-burger-menu :on-open="mobileOpenCallback" :on-close="mobileCloseCallback">
        <template #content>
          <ul class="flex flex-col divide-y divide-secondary">
            <li v-if="signedIn">
              <a :href="accountUrl" class="flex space-x-4 py-8 text-typo no-underline">
                <p-avatar class="flex-shrink-0">{{ initals }}</p-avatar>
                <span class="flex flex-col divide-none overflow-hidden justify-center">
                  <span v-if="displayName != email" class="font-bold">{{ displayName }}</span>
                  <span class="truncate flex-shrink-1">{{ email }}</span>
                </span>
              </a>
            </li>

            <li v-if="signedIn">
              <div class="py-8">
                <span class="text-brand-typo font-bold text-2xl">
                  {{ I18n.t("dashboard.credits", { count: credits }) }}
                </span>
                <p-link :href="pricingUrl" hide-arrow>{{ I18n.t("dashboard.buy_credits_or_subscribe") }}</p-link>
              </div>
            </li>

            <li>
              <p-nav-link :href="newImageUrl" height="lg">
                {{ I18n.t("navigation.remove_background") }}
              </p-nav-link>
            </li>
            <li>
              <p-accordion-group>
                <p-accordion>
                  <template #title>
                    {{ I18n.t("navigation.how_to_use") }}
                  </template>

                  <template #content>
                    <p-sub-nav-link v-for="targetGroup in targetGroups" :key="targetGroup.title" :href="targetGroup.href">
                      <template #icon>
                        <component :is="targetGroup.icon" />
                      </template>
                      <template #title> {{ targetGroup.title }} </template>
                    </p-sub-nav-link>
                  </template>
                </p-accordion>
              </p-accordion-group>
            </li>

            <li>
              <p-nav-link height="lg" :href="toolsUrl">
                {{ I18n.t("navigation.tools_api") }}
              </p-nav-link>
            </li>
            <li>
              <p-nav-link height="lg" :href="pricingUrl">
                {{ I18n.t("navigation.pricing") }}
                <p-badge v-if="blackFriday" class="ml-1" tone="attentive">Black Friday</p-badge>
              </p-nav-link>
            </li>

            <li v-if="!signedIn">
              <p-nav-link height="lg" :href="loginUrl">{{ I18n.t("navigation.login") }}</p-nav-link>
            </li>
            <li v-if="signedIn">
              <p-nav-link height="lg" data-method="delete" :href="logoutUrl">{{ I18n.t("navigation.logout") }}</p-nav-link>
            </li>
          </ul>
          <ul v-if="!signedIn" class="flex flex-col divide-y divide-secondary">
            <li class="w-full">
              <p-button v-if="!signedIn" as="link" :full-width="true" variant="secondary" :href="signupUrl">{{ I18n.t("navigation.signup") }} </p-button>
            </li>
          </ul>
        </template>
      </p-burger-menu>
    </div>
  </p-nav>
</template>

<script setup>
import "@/src/i18n";
import { ref, inject } from "vue";
import emitter from "@/modules/event_bus";

const mobileNavOpen = ref(false);
const credits = ref(0);
const loaded = ref(false);
const I18n = inject("I18n");

const props = defineProps({
  signedIn: Boolean,
  blackFriday: Boolean,
  initals: String,
  displayName: String,
  email: String,
  rootUrl: String,
  newImageUrl: String,
  creditsInfoUrl: String,
  pricingUrl: String,
  signupUrl: String,
  loginUrl: String,
  logoutUrl: String,
  accountUrl: String,
  toolsUrl: String,
  pricingUrl: String,
  targetGroups: Array,
});

const mobileOpenCallback = () => {
  mobileNavOpen.value = true;
  document.body.classList.add("lock-scroll");
  loadCredits();
};

const mobileCloseCallback = () => {
  document.body.classList.remove("lock-scroll");
  mobileNavOpen.value = false;
};

const loadCredits = () => {
  if (loaded.value === true) return;
  if (!props.signedIn) return;

  fetch(props.creditsInfoUrl)
    .then((res) => res.json())
    .then((data) => {
      credits.value = data.credits;
      loaded.value = true;
    });
};

emitter.on("purchase", () => {
  loaded.value = false;
});
</script>

<style>
.lock-scroll {
  overflow: hidden;
}
</style>
